/**
 * How far the policy in force has actually reached the fleet — Task U15, KTD14.
 *
 * `GET /policies/{id}/rollout` answers four counts. They are **four questions, not
 * four slices**, and this panel is written so that a reader cannot mistake them for a
 * pie chart: `total` is printed beside them with the sentence that says they need not
 * add up, because a machine that acknowledged the version and then went dark is
 * honestly both acknowledged and offline, and hiding one of those two facts to make
 * the numbers tidy is the failure this whole shape exists to avoid.
 *
 * ── `pending` is the one with a clock in it ─────────────────────────────────
 *
 * KTD14's rule, read at this level: a machine that was served the version and has not
 * been heard from since is not a rollout in progress, it is a silent agent. The
 * server counts it as `stale` or `offline` and not as `pending`, and the wording here
 * says so — an administrator watching a `pending` number go down is watching machines
 * that are actually there.
 *
 * ── Two reads, two permissions, and the second is not hidden by an `if` ─────
 *
 * The counts are `workforce.manage_policies`. Naming the machines behind them needs
 * `workforce.manage_devices` as well, and the two codes are independent. The list
 * request is not made when the session does not report the second code — that is a
 * courtesy, exactly as `nav/menu.ts` describes its own gate: hiding a control is not
 * a permission check, the server is the authority, and a `403` from it renders
 * through the ordinary `ApiErrorNote` path rather than being swallowed.
 *
 * ── A count is never a dash, and a dash is never a zero ─────────────────────
 *
 * While the read is in flight the panel renders a skeleton, not zeroes. "No machine
 * has acknowledged this policy" and "we have not asked yet" are two different facts
 * about a company's fleet, and D-22 on this build is what happens when the second is
 * rendered as the first.
 *
 * ── The counts belong to a version string, and the version string moves ─────
 *
 * Review round 1 found the worst version of exactly the failure this panel exists to
 * prevent, one level up from the buckets. `useApiQuery` is keyed on its **path**, and
 * a `PATCH` to the active policy mints a new `policy_version` **without changing the
 * id** — so the path does not move, the counts are never re-read, and meanwhile the
 * hook refetches the list and `props.policy.policy_version` becomes the new string.
 * An administrator saved, and read "counted against 2026.09.07-2 — acknowledged 40"
 * about a version no machine had ever been served.
 *
 * Two fixes, and both are needed. The sentence prints **the response's own**
 * `policy_version`, so what is on screen is always the string the numbers beside it
 * were counted against, even mid-flight. And a `reloadToken` derived from the active
 * version re-reads the counts when that string moves — which is the case
 * `useApiQuery`'s own `reloadToken` docstring exists for.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import { formatInstant } from '../../i18n/datetime';
import {
  policyRolloutDevicesPath,
  policyRolloutPath,
  type DeviceLiveness,
  type Policy,
  type PolicyRollout,
  type PolicyRolloutDeviceListResponse,
} from '../../api/policies';
import { useApiQuery } from '../../api/useApiQuery';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';

export interface RolloutPanelProps {
  /** The version in force, or `null` when this company has none. */
  readonly policy: Policy | null;
  /** Does the session also report `workforce.manage_devices`? */
  readonly canSeeDevices: boolean;
  readonly locale: string;
  readonly timeZone: string;
}

/** The four counts, in the order an administrator reads them: good news first. */
const BUCKETS = ['acknowledged', 'pending', 'stale', 'offline'] as const;

type Bucket = (typeof BUCKETS)[number];

/**
 * Exhaustive by type, for `CollectionRules`' own reason: a fifth bucket added to the
 * response must be a compile error here rather than a column that silently renders
 * nothing.
 */
const BUCKET_LABEL: Readonly<Record<Bucket, MessageKey>> = {
  acknowledged: 'policyEditor.rollout.acknowledged',
  pending: 'policyEditor.rollout.pending',
  stale: 'policyEditor.rollout.stale',
  offline: 'policyEditor.rollout.offline',
};

/** The same discipline for the three liveness bands `deviceLiveness()` returns. */
const LIVENESS_LABEL: Readonly<Record<DeviceLiveness, MessageKey>> = {
  online: 'policyEditor.rollout.online',
  stale: 'policyEditor.rollout.stale',
  offline: 'policyEditor.rollout.offline',
};

export function RolloutPanel(props: RolloutPanelProps): ReactNode {
  const { t } = useI18n();
  const policyId = props.policy?.id ?? null;
  const activeVersion = props.policy?.policy_version ?? null;

  /*
    A counter that moves when the ACTIVE VERSION STRING moves, not when the component
    renders. `useApiQuery`'s `reloadToken` is a number, so the version string cannot
    be passed directly; a hash of it could collide, and on a screen whose thesis is
    that a number must never be quietly wrong, "astronomically unlikely" is the wrong
    kind of argument. So the previous string is remembered and compared.

    The FIRST non-null version does not bump it, and that matters: the list read
    resolves `policy` from `null` to a row, which moves the path from `null` to a real
    one and fires the read on its own. Bumping there too would make every mount send
    two identical requests. So the token moves only when one **known** version string
    is replaced by another — which is exactly what a landed patch does.
  */
  const seenVersion = useRef<string | null>(null);
  const [versionToken, setVersionToken] = useState(0);
  useEffect(() => {
    const previous = seenVersion.current;
    seenVersion.current = activeVersion;
    if (previous === null || previous === activeVersion) return;
    setVersionToken((value) => value + 1);
  }, [activeVersion]);

  // `null` parks `useApiQuery` in `loading` with no request in flight, which is its
  // own convention for "there is nothing to ask yet" — not an empty result.
  const rollout = useApiQuery<PolicyRollout>(
    policyId === null ? null : policyRolloutPath(policyId),
    versionToken,
  );
  const devices = useApiQuery<PolicyRolloutDeviceListResponse>(
    policyId === null || !props.canSeeDevices ? null : policyRolloutDevicesPath(policyId),
    versionToken,
  );

  return (
    <section className="wp-section" aria-labelledby="u15-rollout-heading" data-testid="u15-rollout">
      <h2 className="wp-section__title" id="u15-rollout-heading">
        {t('policyEditor.rollout.title')}
      </h2>

      {props.policy === null ? (
        <EmptyState
          title={t('policyEditor.rollout.noActive.title')}
          description={t('policyEditor.rollout.noActive.body')}
          testId="u15-rollout-none"
        />
      ) : (
        <>
          {rollout.status === 'loading' ? (
            <Skeleton
              rows={2}
              variant="block"
              label={t('policyEditor.rollout.subject')}
              testId="u15-rollout-skeleton"
            />
          ) : null}

          {rollout.status === 'error' && rollout.error !== null ? (
            <ApiErrorNote
              error={rollout.error}
              onRetry={rollout.reload}
              subject={t('policyEditor.rollout.subject')}
              testId="u15-rollout-error"
            />
          ) : null}

          {rollout.data === null ? null : (
            <>
              {/*
                The response's own `policy_version`, never `props.policy.policy_version`.
                The two differ for exactly as long as a re-read is in flight after a
                patch, and during that window the prop is the NEW string while the
                numbers below are still the old version's — see this file's header.
              */}
              <p className="wp-small wp-muted" data-testid="u15-rollout-against">
                {t('policyEditor.rollout.against', { version: rollout.data.policy_version })}
              </p>

              {/* The five buckets add up to `total`, so they are read as a ledger:
                  counts right-aligned in tabular figures and the total on its own
                  heavier rule. A column of counts that does not line up is a column
                  nobody can add in their head (Luật Số Thẳng Cột). */}
              <div className="wp-table-scroll">
                <table className="wp-table wp-table--fluid wp-ledger" data-testid="u15-rollout-table">
                  <caption className="wp-visually-hidden">
                    {t('policyEditor.rollout.caption')}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('policyEditor.rollout.column.bucket')}</th>
                      <th scope="col" className="wp-ledger__amount">
                        {t('policyEditor.rollout.column.count')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {BUCKETS.map((bucket) => (
                      <tr key={bucket}>
                        <th scope="row">{t(BUCKET_LABEL[bucket])}</th>
                        <td className="wp-ledger__amount" data-testid={`u15-rollout-${bucket}`}>
                          {rollout.data?.[bucket] ?? 0}
                        </td>
                      </tr>
                    ))}
                    <tr className="wp-ledger__line--total">
                      <th scope="row">{t('policyEditor.rollout.total')}</th>
                      <td className="wp-ledger__amount" data-testid="u15-rollout-total">
                        {rollout.data.total}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="wp-small wp-muted" data-testid="u15-rollout-overlap">
                {t('policyEditor.rollout.overlap')}
              </p>
              <p className="wp-small wp-muted" data-testid="u15-rollout-asof">
                {t('policyEditor.rollout.asOf', {
                  at: formatInstant(rollout.data.as_of, props.locale, props.timeZone),
                })}
              </p>
            </>
          )}

          {/* The per-device list, and the sentence that explains its absence. */}
          {props.canSeeDevices ? (
            <>
              {devices.status === 'loading' ? (
                <Skeleton
                  rows={3}
                  variant="block"
                  label={t('policyEditor.rollout.devices.subject')}
                  testId="u15-devices-skeleton"
                />
              ) : null}

              {devices.status === 'error' && devices.error !== null ? (
                <ApiErrorNote
                  error={devices.error}
                  onRetry={devices.reload}
                  subject={t('policyEditor.rollout.devices.subject')}
                  testId="u15-devices-error"
                />
              ) : null}

              {devices.data === null ? null : devices.data.data.length === 0 ? (
                <EmptyState
                  title={t('policyEditor.rollout.devices.empty.title')}
                  description={t('policyEditor.rollout.devices.empty.body')}
                  testId="u15-devices-empty"
                />
              ) : (
                <div className="wp-table-scroll">
                  <table className="wp-table" data-testid="u15-devices-table">
                    <caption className="wp-visually-hidden">
                      {t('policyEditor.rollout.devices.caption')}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('policyEditor.rollout.devices.host')}</th>
                        <th scope="col">{t('policyEditor.rollout.devices.liveness')}</th>
                        <th scope="col">{t('policyEditor.rollout.devices.served')}</th>
                        <th scope="col">{t('policyEditor.rollout.devices.acked')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devices.data.data.map((device) => (
                        <tr key={device.device_id} data-testid={`u15-device-${device.hostname}`}>
                          <th scope="row">{device.hostname}</th>
                          <td>
                            <Badge
                              severity={device.liveness === 'online' ? 'low' : 'medium'}
                              icon={device.liveness === 'online' ? 'circle-check' : 'clock'}
                              label={t(LIVENESS_LABEL[device.liveness])}
                              testId={`u15-device-liveness-${device.hostname}`}
                            />
                          </td>
                          {/* `null` is said out loud. A machine that has never been
                            served the policy and one that was served it are two
                            different situations, and an empty cell would erase the
                            difference. */}
                          <td className="wp-small">
                            {device.current_policy_version ??
                              t('policyEditor.rollout.devices.never')}
                          </td>
                          <td className="wp-small">
                            {device.acked_policy_version ?? t('policyEditor.rollout.devices.never')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/*
                The endpoint pages at 50, and a fleet with more than 50 machines
                holding the policy up would otherwise be silently truncated — on a
                panel whose whole argument is that a number must never be quietly
                wrong. So the two numbers are printed side by side, and the shortfall
                is named rather than left to be inferred from them.
              */}
              {devices.data === null ? null : (
                <p className="wp-small wp-muted" data-testid="u15-devices-count">
                  {t('policyEditor.rollout.devices.showing', {
                    shown: devices.data.data.length,
                    total: devices.data.total,
                  })}
                  {devices.data.data.length < devices.data.total
                    ? ` ${t('policyEditor.rollout.devices.truncated')}`
                    : ''}
                </p>
              )}
            </>
          ) : (
            <p className="wp-small wp-muted" data-testid="u15-devices-withheld">
              {t('policyEditor.rollout.devices.withheld')}
            </p>
          )}
        </>
      )}
    </section>
  );
}
